// A zip file, written by hand.
//
// The Room Names export is forty small CSVs and a JSON sidecar, which the
// operator unpacks into a folder of per-building sheets; a single download is
// the whole point. There is no zip library in this project and adding one for
// a few kilobytes of text would be the heaviest dependency on the page. The
// STORED method (no compression) is a local header, the bytes, a central
// directory and an end record — the entire format fits below, and the CSVs
// are small enough that compression would save nothing anybody notices.
//
// Pure: `node src/utils/zip.test.mjs` checks the CRC against the standard
// vector and that Python's zipfile reads what comes out.
//
// `readZip` is the other direction, added for the Tour Creator's "Restore from
// a backup" screen, which reads the export zip back in the browser. It reads
// STORED entries (what `buildZip` writes) and DEFLATED ones (what macOS
// "Compress", Python's zipfile and most tools write when somebody re-packs the
// folder), the latter through the Web `DecompressionStream`, so there is still
// no zip dependency. Zip64 is not supported — an export is a few MB of JSON
// and photos, nowhere near 4 GB — and says so rather than reading garbage.

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes) {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

const encode = (s) => new TextEncoder().encode(String(s));

/** MS-DOS time and date words, which is all the format has room for. */
function dosStamp(date) {
  const d = date instanceof Date ? date : new Date();
  const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
  const day = ((Math.max(1980, d.getFullYear()) - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  return { time: time & 0xFFFF, day: day & 0xFFFF };
}

/**
 * `files` is `[{ path, data }]` — `data` a string (UTF-8 encoded here) or a
 * Uint8Array. Returns the zip as one Uint8Array.
 */
export function buildZip(files, { date } = {}) {
  const { time, day } = dosStamp(date);
  const locals = [], centrals = [];
  let offset = 0;
  for (const f of files) {
    const name = encode(f.path);
    const data = typeof f.data === 'string' ? encode(f.data) : f.data;
    const crc = crc32(data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);        // version needed: 2.0
    local.setUint16(6, 0x0800, true);    // flags: names are UTF-8
    local.setUint16(8, 0, true);         // method: stored
    local.setUint16(10, time, true);
    local.setUint16(12, day, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true);      // made by
    central.setUint16(6, 20, true);      // needed
    central.setUint16(8, 0x0800, true);
    central.setUint16(10, 0, true);
    central.setUint16(12, time, true);
    central.setUint16(14, day, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, data.length, true);
    central.setUint32(24, data.length, true);
    central.setUint16(28, name.length, true);
    central.setUint16(30, 0, true);      // extra
    central.setUint16(32, 0, true);      // comment
    central.setUint16(34, 0, true);      // disk
    central.setUint16(36, 0, true);      // internal attrs
    central.setUint32(38, 0, true);      // external attrs
    central.setUint32(42, offset, true);
    locals.push(new Uint8Array(local.buffer), name, data);
    centrals.push(new Uint8Array(central.buffer), name);
    offset += 30 + name.length + data.length;
  }
  const cdSize = centrals.reduce((n, b) => n + b.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(4, 0, true);
  end.setUint16(6, 0, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, cdSize, true);
  end.setUint32(16, offset, true);
  end.setUint16(20, 0, true);
  const parts = [...locals, ...centrals, new Uint8Array(end.buffer)];
  const out = new Uint8Array(parts.reduce((n, b) => n + b.length, 0));
  let at = 0;
  for (const p of parts) { out.set(p, at); at += p.length; }
  return out;
}

// ── Reading ──────────────────────────────────────────────────────────────────

const SIG_LOCAL = 0x04034b50, SIG_CENTRAL = 0x02014b50, SIG_END = 0x06054b50;
const MAX_COMMENT = 0xFFFF; // the EOCD may be followed by a comment of at most this length

/** Inflate a raw DEFLATE stream (zip method 8) with the platform's decoder. */
async function inflateRaw(raw) {
  if (typeof DecompressionStream !== 'function') {
    throw new Error('This browser cannot read compressed zip entries; try a zip saved without compression.');
  }
  const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Parse a zip into `[{ path, data: Uint8Array }]`, directory entries skipped.
 * `bytes` is a Uint8Array or ArrayBuffer. Names are decoded as UTF-8 whether
 * or not the entry sets the UTF-8 flag (ASCII decodes identically, and every
 * tool that writes a non-ASCII name today writes it as UTF-8). Throws a plain
 * Error for anything that is not a zip, is Zip64, encrypted, or uses a
 * compression method other than stored/deflate, and for an entry whose bytes
 * fail their own CRC.
 */
export async function readZip(bytes) {
  const buf = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 22 - MAX_COMMENT); i--) {
    if (view.getUint32(i, true) === SIG_END) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('This is not a zip file.');
  const count = view.getUint16(eocd + 10, true);
  const cdSize = view.getUint32(eocd + 12, true);
  const cdOffset = view.getUint32(eocd + 16, true);
  if (count === 0xFFFF || cdSize === 0xFFFFFFFF || cdOffset === 0xFFFFFFFF) {
    throw new Error('This zip uses Zip64, which is not supported here.');
  }
  if (cdOffset + cdSize > buf.length) throw new Error('This zip is truncated or corrupt (central directory out of range).');

  const decoder = new TextDecoder('utf-8');
  const out = [];
  let p = cdOffset;
  for (let i = 0; i < count; i++) {
    if (p + 46 > buf.length || view.getUint32(p, true) !== SIG_CENTRAL) {
      throw new Error('This zip is corrupt (bad central directory entry).');
    }
    const flags = view.getUint16(p + 8, true);
    const method = view.getUint16(p + 10, true);
    const crc = view.getUint32(p + 16, true);
    const csize = view.getUint32(p + 20, true);
    const usize = view.getUint32(p + 24, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localOffset = view.getUint32(p + 42, true);
    const path = decoder.decode(buf.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    if (path.endsWith('/')) continue; // a directory entry carries no bytes
    if (csize === 0xFFFFFFFF || usize === 0xFFFFFFFF || localOffset === 0xFFFFFFFF) {
      throw new Error(`"${path}" uses Zip64, which is not supported here.`);
    }
    if (flags & 0x0001) throw new Error(`"${path}" is encrypted; this reader cannot open password-protected zips.`);
    if (method !== 0 && method !== 8) throw new Error(`"${path}" uses compression method ${method}; only stored and deflate are supported.`);
    if (localOffset + 30 > buf.length || view.getUint32(localOffset, true) !== SIG_LOCAL) {
      throw new Error(`This zip is corrupt (bad local header for "${path}").`);
    }
    // The local header's name/extra lengths can differ from the central
    // directory's (the extra field often does), so measure the data from it.
    const start = localOffset + 30 + view.getUint16(localOffset + 26, true) + view.getUint16(localOffset + 28, true);
    if (start + csize > buf.length) throw new Error(`This zip is truncated ("${path}" runs past the end).`);
    const raw = buf.subarray(start, start + csize);
    const data = method === 0 ? raw.slice() : await inflateRaw(raw);
    if (data.length !== usize) throw new Error(`"${path}" did not decompress to its recorded size.`);
    if (crc32(data) !== crc) throw new Error(`"${path}" failed its checksum; the zip is corrupt.`);
    out.push({ path, data });
  }
  return out;
}
